import {
  installDateOverrideFrom,
  jobCardInstallSpan,
  type InstallCalendarEvent,
  type InstallSpan,
} from './jobCardInstallDate';

export type CoverageCard = {
  id: string;
  title?: string | null;
  install_date_override?: string | null;
  install_end_date_override?: string | null;
  follows_booking?: boolean | null;
};

export type CardSpan = {
  cardId: string;
  title: string;
  span: InstallSpan;
};

export function cardInstallSpans(
  calendar: InstallCalendarEvent | null,
  cards: CoverageCard[],
  project: { metadata?: unknown } = {},
): CardSpan[] {
  return cards.map((card) => ({
    cardId: card.id,
    title: card.title || 'Job card',
    span: jobCardInstallSpan({
      calendar,
      overrideStart: installDateOverrideFrom(
        { id: card.id, install_date_override: card.install_date_override ?? null },
        project,
      ),
      overrideEnd: card.install_end_date_override ?? null,
      followsBooking: card.follows_booking !== false,
    }),
  }));
}

export type DayOverlap = { days: string[]; titles: string[] };

export type InstallCoverage = {
  /** Calendar invite days that no job card covers. */
  uncovered: string[];
  /** Days more than one card covers, where at least one of them has a date override. */
  overlaps: DayOverlap[];
};

export function installCoverage(calendar: InstallCalendarEvent | null, spans: CardSpan[]): InstallCoverage {
  if (!spans.length) return { uncovered: [], overlaps: [] };
  const calendarDays = jobCardInstallSpan({ calendar }).days;
  const coveredBy = new Map<string, CardSpan[]>();
  spans.forEach((entry) => {
    entry.span.days.forEach((day) => {
      coveredBy.set(day, [...(coveredBy.get(day) || []), entry]);
    });
  });
  const uncovered = calendarDays.filter((day) => !coveredBy.has(day));
  const overlaps: DayOverlap[] = [];
  Array.from(coveredBy.keys()).sort().forEach((day) => {
    const entries = coveredBy.get(day) || [];
    if (entries.length < 2 || !entries.some((entry) => entry.span.overridden)) return;
    const titles = entries.map((entry) => entry.title);
    const last = overlaps[overlaps.length - 1];
    if (last && last.titles.join('|') === titles.join('|')) last.days.push(day);
    else overlaps.push({ days: [day], titles });
  });
  return { uncovered, overlaps };
}

export type CrewScheduleRow = {
  cardId: string;
  title: string;
  crew: string[];
  days: string[];
  crewDays: number;
};

export type CrewSchedule = {
  rows: CrewScheduleRow[];
  /** Distinct days anyone was booked on site. */
  siteDays: number;
  /** Sum of people × days across every card. */
  crewDays: number;
};

export function buildCrewSchedule(
  spans: CardSpan[],
  assignees: Array<{ job_card_id: string; full_name?: string | null; email?: string | null }>,
): CrewSchedule {
  const rows = spans.map((entry) => {
    const crew = assignees
      .filter((row) => row.job_card_id === entry.cardId)
      .map((row) => row.full_name || row.email || 'Unnamed');
    return {
      cardId: entry.cardId,
      title: entry.title,
      crew,
      days: entry.span.days,
      crewDays: crew.length * entry.span.days.length,
    };
  });
  return {
    rows,
    siteDays: new Set(rows.flatMap((row) => row.days)).size,
    crewDays: rows.reduce((sum, row) => sum + row.crewDays, 0),
  };
}
